import React, { useState } from "react";
import { FormGroup, Label, Input, Row, Col } from "reactstrap";

/* packages */
import { Buttons, Invalid } from "../../lib-atomic";
import { LocalStorage, FetchContextApi } from "../../lib-atomic-functions";
import { ModuleText } from "../../lib-atomic-design-section";
/* packages */

/* stores */
import { OrderCollect } from "../../app-module-order-container/stores";
/* stores */

import CONFIG from "../config";

/* CONSTANTS */
const COLLECT = CONFIG.application.order.api.endpoints.collect;
const COLLECTCONFIG = {
  url: COLLECT.url,
  method: COLLECT.method,
  headers: COLLECT.headers,
  responseType: "json",
};
const EMPTY_RANGE = { start: "", end: "", count: "" };
/* CONSTANTS */

const rangeSize = (range) => {
  const start = Number(range.start);
  const end = Number(range.end);
  if (!range.start || !range.end || isNaN(start) || isNaN(end)) return 0;
  return end - start + 1;
};

const index = ({ props, order, product_card }) => {
  const { token } = LocalStorage.getLS().login;
  const orderId = order.id;
  const requiredCount = product_card.products.reduce(
    (a, b) => a + (b.quantity || 0),
    0
  );

  const [splitRange, setSplitRange] = useState(false);

  /* swipe mode */
  const [swipeInput, setSwipeInput] = useState("");
  const [swipedCards, setSwipedCards] = useState([]);

  /* split range mode */
  const [ranges, setRanges] = useState([{ ...EMPTY_RANGE }]);

  const [submitError, setSubmitError] = useState(false);

  /* states */
  COLLECTCONFIG.headers = { ...COLLECTCONFIG.headers, Authorization: token };
  const [orderCollectContext, orderCollectMethod] = FetchContextApi(
    COLLECTCONFIG,
    OrderCollect
  );
  /* states */

  const handleToggleSplitRange = () => {
    setSplitRange(!splitRange);
  };

  const handleSwipe = (event) => {
    if (event.key !== "Enter") return;
    event.preventDefault();
    const card = swipeInput.trim();
    if (!card) return;
    setSwipeInput("");
    if (swipedCards.includes(card)) return; // ponytail: ignore duplicate swipes rather than erroring
    setSwipedCards([...swipedCards, card]);
  };

  const handleRemoveSwiped = (card) => {
    setSwipedCards(swipedCards.filter((c) => c !== card));
  };

  const handleRangeChange = (i, field, value) => {
    const next = [...ranges];
    next[i] = { ...next[i], [field]: value };
    setRanges(next);
  };

  const handleAddRange = () => {
    setRanges([...ranges, { ...EMPTY_RANGE }]);
  };

  const handleRemoveRange = (i) => {
    setRanges(ranges.filter((_, index) => index !== i));
  };

  const rangeMismatches = splitRange
    ? ranges.filter(
        (range) => range.count !== "" && rangeSize(range) !== Number(range.count)
      )
    : [];

  const collectedCount = splitRange
    ? ranges.reduce((total, range) => total + rangeSize(range), 0)
    : swipedCards.length;

  const countMatches = requiredCount > 0 && collectedCount === requiredCount;
  const canConfirm = countMatches && rangeMismatches.length === 0;

  const handleConfirm = async () => {
    if (!canConfirm) return;
    const bodyData = splitRange
      ? {
          ranges: ranges.map((range) => ({
            startCardNumber: range.start,
            endCardNumber: range.end,
            quantity: Number(range.count),
          })),
        }
      : { cards: swipedCards };

    await orderCollectMethod({
      query: orderId + "/collect",
      body: JSON.stringify(bodyData),
    });

    if (orderCollectContext.status) {
      props.history.push("/orders/detail/" + orderId);
    } else {
      setSubmitError(true);
    }
  };

  const handleCancel = () => {
    props.history.push("/orders/detail/" + orderId);
  };

  return (
    <div>
      <ModuleText>
        Swipe or enter the {requiredCount} card{requiredCount === 1 ? "" : "s"}{" "}
        being handed over for order <b>{orderId}</b>.
      </ModuleText>

      <FormGroup check className="space-standard-top">
        <Label check>
          <Input
            type="checkbox"
            checked={splitRange}
            onChange={handleToggleSplitRange}
          />{" "}
          Split Range
        </Label>
      </FormGroup>

      {!splitRange && (
        <FormGroup className="space-standard-top">
          <Label for="swipe">Swipe card</Label>
          <Input
            id="swipe"
            autoFocus
            value={swipeInput}
            onChange={(event) => setSwipeInput(event.target.value)}
            onKeyDown={handleSwipe}
            placeholder="Swipe or type card number, then press Enter"
          />
          <ul>
            {swipedCards.map((card) => (
              <li key={card}>
                {card}{" "}
                <Buttons.Text onClick={() => handleRemoveSwiped(card)}>
                  Remove
                </Buttons.Text>
              </li>
            ))}
          </ul>
        </FormGroup>
      )}

      {splitRange && (
        <div className="space-standard-top">
          {ranges.map((range, i) => (
            <Row key={i} className="space-semi-top">
              <Col>
                <Label>Start Range</Label>
                <Input
                  value={range.start}
                  onChange={(event) =>
                    handleRangeChange(i, "start", event.target.value)
                  }
                />
              </Col>
              <Col>
                <Label>End Range</Label>
                <Input
                  value={range.end}
                  onChange={(event) =>
                    handleRangeChange(i, "end", event.target.value)
                  }
                />
              </Col>
              <Col>
                <Label>Count</Label>
                <Input
                  value={range.count}
                  onChange={(event) =>
                    handleRangeChange(i, "count", event.target.value)
                  }
                />
              </Col>
              <Col xs="auto" className="d-flex align-items-end">
                {ranges.length > 1 && (
                  <Buttons.Text onClick={() => handleRemoveRange(i)}>
                    Remove
                  </Buttons.Text>
                )}
              </Col>
            </Row>
          ))}
          <Buttons.Text onClick={handleAddRange}>
            + Add another range
          </Buttons.Text>
        </div>
      )}

      <hr />
      <ModuleText>
        <b>{collectedCount}</b> of <b>{requiredCount}</b> cards accounted for.
      </ModuleText>

      {rangeMismatches.length > 0 && (
        <Invalid error={["One or more ranges don't match their entered count."]} />
      )}
      {!countMatches && (
        <Invalid error={[`Card count must equal ${requiredCount} before confirming.`]} />
      )}
      {submitError && (
        <Invalid error={["Failed to confirm collection. Please try again."]} />
      )}

      <div className="space-standard-top">
        <Buttons.Primary disabled={!canConfirm} onClick={handleConfirm}>
          Confirm Collection
        </Buttons.Primary>{" "}
        <Buttons.Text onClick={handleCancel}>Cancel</Buttons.Text>
      </div>
    </div>
  );
};

export default index;
